/**
 * LocaleRoute — sets the active i18n locale from the URL, then renders
 * its children unchanged. Designed to wrap any page component that lives
 * at a language-prefixed URL (e.g. /fr → landing in French).
 *
 * Priority order (mirrors LanguageCapsule's `change` fn):
 *   1. lang prop from the URL segment (always wins for these routes)
 *   2. Persists choice to localStorage (same key as LanguageCapsule)
 *   3. Marks the choice as "explicit" so auth_context never reverts it
 *
 * Also sets document.documentElement.lang immediately so screen-readers
 * and bots that execute JS see the correct language attribute.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_STORAGE_KEY, LANGUAGE_EXPLICIT_KEY } from '@/i18n/config';

export default function LocaleRoute({ lang, children }) {
  const { i18n } = useTranslation();

  useEffect(() => {
    const active = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
    if (active === lang) return; // already correct
    i18n.changeLanguage(lang).catch(() => {});
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      window.localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');
    } catch { /* quota / private-mode */ }
    // Sync HTML lang attribute immediately (i18n 'languageChanged' will
    // also fire, but this ensures bots that pause JS execution early see
    // the correct value before the event fires).
    document.documentElement.lang = lang;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return children;
}
